/**
 * World currencies list (ISO 4217).
 * Each entry: code (3-letter), full name, and sign for display.
 * Sign uses the currency symbol when one exists; otherwise the 3-letter code.
 */

import { Currency } from "../domain/model"


function signFrom(code: string, symbol: string): string {
  const useSymbol =
    symbol.length <= 2 &&
    !/^[A-Za-z]{2,3}$/.test(symbol) &&
    !/^[A-Z]{2}$/.test(symbol)
  return useSymbol ? symbol : code;
}

// Raw data: code -> { name, symbol } (symbol may be symbol or symbolNative from ISO sources).
const RAW: Record<string, { name: string; symbol: string }> = {
  AED: { name: 'United Arab Emirates Dirham', symbol: 'د.إ.' },
  AFN: { name: 'Afghan Afghani', symbol: 'AFN' },
  ALL: { name: 'Albanian Lek', symbol: 'ALL' },
  AMD: { name: 'Armenian Dram', symbol: '֏' },
  ANG: { name: 'Netherlands Antillean Guilder', symbol: 'ƒ' },
  AOA: { name: 'Angolan Kwanza', symbol: 'AOA' },
  ARS: { name: 'Argentine Peso', symbol: 'ARS' },
  AUD: { name: 'Australian Dollar', symbol: 'AUD' },
  AWG: { name: 'Aruban Florin', symbol: 'ƒ' },
  AZN: { name: 'Azerbaijani Manat', symbol: '₼' },
  BAM: { name: 'Bosnia and Herzegovina Convertible Mark', symbol: 'BAM' },
  BBD: { name: 'Barbadian Dollar', symbol: 'BBD' },
  BDT: { name: 'Bangladeshi Taka', symbol: '৳' },
  BGN: { name: 'Bulgarian Lev', symbol: 'BGN' },
  BHD: { name: 'Bahraini Dinar', symbol: 'BHD' },
  BIF: { name: 'Burundian Franc', symbol: 'BIF' },
  BMD: { name: 'Bermudian Dollar', symbol: 'BMD' },
  BND: { name: 'Brunei Dollar', symbol: 'BND' },
  BOB: { name: 'Bolivian Boliviano', symbol: 'Bs.' },
  BRL: { name: 'Brazilian Real', symbol: 'R$' },
  BSD: { name: 'Bahamian Dollar', symbol: 'BSD' },
  BTN: { name: 'Bhutanese Ngultrum', symbol: 'BTN' },
  BWP: { name: 'Botswana Pula', symbol: 'BWP' },
  BYN: { name: 'Belarusian Ruble', symbol: 'BYN' },
  BZD: { name: 'Belize Dollar', symbol: 'BZD' },
  CAD: { name: 'Canadian Dollar', symbol: 'CAD' },
  CDF: { name: 'Congolese Franc', symbol: '₣' },
  CHF: { name: 'Swiss Franc', symbol: '₣' },
  CLP: { name: 'Chilean Peso', symbol: 'CLP' },
  CNY: { name: 'Chinese Yuan', symbol: '¥' },
  COP: { name: 'Colombian Peso', symbol: 'COP' },
  CRC: { name: 'Costa Rican Colon', symbol: '₡' },
  CUP: { name: 'Cuban Peso', symbol: '₱' },
  CVE: { name: 'Cabo Verdean Escudo', symbol: 'CVE' },
  CZK: { name: 'Czech Koruna', symbol: 'Kč' },
  DJF: { name: 'Djiboutian Franc', symbol: 'DJF' },
  DKK: { name: 'Danish Krone', symbol: 'kr.' },
  DOP: { name: 'Dominican Peso', symbol: 'DOP' },
  DZD: { name: 'Algerian Dinar', symbol: 'DZD' },
  EGP: { name: 'Egyptian Pound', symbol: 'EGP' },
  ERN: { name: 'Eritrean Nakfa', symbol: 'ERN' },
  ETB: { name: 'Ethiopian Birr', symbol: 'ETB' },
  EUR: { name: 'Euro', symbol: '€' },
  FJD: { name: 'Fijian Dollar', symbol: 'FJD' },
  FKP: { name: 'Falkland Islands Pound', symbol: '£' },
  GBP: { name: 'Pound Sterling', symbol: '£' },
  GEL: { name: 'Georgian Lari', symbol: '₾' },
  GHS: { name: 'Ghanaian Cedi', symbol: '₵' },
  GIP: { name: 'Gibraltar Pound', symbol: '£' },
  GMD: { name: 'Gambian Dalasi', symbol: 'GMD' },
  GNF: { name: 'Guinean Franc', symbol: 'GNF' },
  GTQ: { name: 'Guatemalan Quetzal', symbol: 'GTQ' },
  GYD: { name: 'Guyanese Dollar', symbol: 'GYD' },
  HKD: { name: 'Hong Kong Dollar', symbol: 'HKD' },
  HNL: { name: 'Honduran Lempira', symbol: 'HNL' },
  HTG: { name: 'Haitian Gourde', symbol: 'HTG' },
  HUF: { name: 'Hungarian Forint', symbol: 'Ft' },
  IDR: { name: 'Indonesian Rupiah', symbol: 'Rp' },
  ILS: { name: 'Israeli New Shekel', symbol: '₪' },
  INR: { name: 'Indian Rupee', symbol: '₹' },
  IQD: { name: 'Iraqi Dinar', symbol: 'IQD' },
  IRR: { name: 'Iranian Rial', symbol: 'IRR' },
  ISK: { name: 'Icelandic Krona', symbol: 'kr' },
  JMD: { name: 'Jamaican Dollar', symbol: 'JMD' },
  JOD: { name: 'Jordanian Dinar', symbol: 'JOD' },
  JPY: { name: 'Japanese Yen', symbol: '¥' },
  KES: { name: 'Kenyan Shilling', symbol: 'KES' },
  KGS: { name: 'Kyrgyzstani Som', symbol: 'KGS' },
  KHR: { name: 'Cambodian Riel', symbol: '៛' },
  KMF: { name: 'Comorian Franc', symbol: 'KMF' },
  KPW: { name: 'North Korean Won', symbol: '₩' },
  KRW: { name: 'South Korean Won', symbol: '₩' },
  KWD: { name: 'Kuwaiti Dinar', symbol: 'KWD' },
  KYD: { name: 'Cayman Islands Dollar', symbol: 'KYD' },
  KZT: { name: 'Kazakhstani Tenge', symbol: '₸' },
  LAK: { name: 'Lao Kip', symbol: '₭' },
  LBP: { name: 'Lebanese Pound', symbol: 'LBP' },
  LKR: { name: 'Sri Lankan Rupee', symbol: 'LKR' },
  LRD: { name: 'Liberian Dollar', symbol: 'LRD' },
  LSL: { name: 'Lesotho Loti', symbol: 'LSL' },
  LYD: { name: 'Libyan Dinar', symbol: 'LYD' },
  MAD: { name: 'Moroccan Dirham', symbol: 'MAD' },
  MDL: { name: 'Moldovan Leu', symbol: 'MDL' },
  MGA: { name: 'Malagasy Ariary', symbol: 'MGA' },
  MKD: { name: 'Macedonian Denar', symbol: 'MKD' },
  MMK: { name: 'Myanmar Kyat', symbol: 'MMK' },
  MNT: { name: 'Mongolian Tögrög', symbol: '₮' },
  MOP: { name: 'Macanese Pataca', symbol: 'MOP' },
  MRU: { name: 'Mauritanian Ouguiya', symbol: 'MRU' },
  MUR: { name: 'Mauritian Rupee', symbol: 'MUR' },
  MVR: { name: 'Maldivian Rufiyaa', symbol: 'MVR' },
  MWK: { name: 'Malawian Kwacha', symbol: 'MWK' },
  MXN: { name: 'Mexican Peso', symbol: 'MXN' },
  MYR: { name: 'Malaysian Ringgit', symbol: 'RM' },
  MZN: { name: 'Mozambican Metical', symbol: 'MZN' },
  NAD: { name: 'Namibian Dollar', symbol: 'NAD' },
  NGN: { name: 'Nigerian Naira', symbol: '₦' },
  NIO: { name: 'Nicaraguan Córdoba', symbol: 'C$' },
  NOK: { name: 'Norwegian Krone', symbol: 'kr' },
  NPR: { name: 'Nepalese Rupee', symbol: 'NPR' },
  NZD: { name: 'New Zealand Dollar', symbol: 'NZD' },
  OMR: { name: 'Omani Rial', symbol: 'OMR' },
  PAB: { name: 'Panamanian Balboa', symbol: 'PAB' },
  PEN: { name: 'Peruvian Sol', symbol: 'PEN' },
  PGK: { name: 'Papua New Guinean Kina', symbol: 'PGK' },
  PHP: { name: 'Philippine Peso', symbol: '₱' },
  PKR: { name: 'Pakistani Rupee', symbol: 'PKR' },
  PLN: { name: 'Polish Zloty', symbol: 'zł' },
  PYG: { name: 'Paraguayan Guaraní', symbol: 'PYG' },
  QAR: { name: 'Qatari Riyal', symbol: 'QAR' },
  RON: { name: 'Romanian Leu', symbol: 'RON' },
  RSD: { name: 'Serbian Dinar', symbol: 'RSD' },
  RUB: { name: 'Russian Ruble', symbol: '₽' },
  RWF: { name: 'Rwandan Franc', symbol: 'RWF' },
  SAR: { name: 'Saudi Riyal', symbol: 'SAR' },
  SBD: { name: 'Solomon Islands Dollar', symbol: 'SBD' },
  SCR: { name: 'Seychellois Rupee', symbol: 'SCR' },
  SDG: { name: 'Sudanese Pound', symbol: 'SDG' },
  SEK: { name: 'Swedish Krona', symbol: 'kr' },
  SGD: { name: 'Singapore Dollar', symbol: 'SGD' },
  SHP: { name: 'Saint Helena Pound', symbol: '£' },
  SLL: { name: 'Sierra Leonean Leone', symbol: 'SLL' },
  SOS: { name: 'Somali Shilling', symbol: 'SOS' },
  SRD: { name: 'Surinamese Dollar', symbol: 'SRD' },
  SSP: { name: 'South Sudanese Pound', symbol: 'SSP' },
  STN: { name: 'Sao Tome and Principe Dobra', symbol: 'STN' },
  SYP: { name: 'Syrian Pound', symbol: 'SYP' },
  SZL: { name: 'Eswatini Lilangeni', symbol: 'SZL' },
  THB: { name: 'Thai Baht', symbol: '฿' },
  TJS: { name: 'Tajikistani Somoni', symbol: 'TJS' },
  TMT: { name: 'Turkmenistani Manat', symbol: 'TMT' },
  TND: { name: 'Tunisian Dinar', symbol: 'TND' },
  TOP: { name: 'Tongan Paʻanga', symbol: 'TOP' },
  TRY: { name: 'Turkish Lira', symbol: '₺' },
  TTD: { name: 'Trinidad and Tobago Dollar', symbol: 'TTD' },
  TWD: { name: 'New Taiwan Dollar', symbol: 'NT$' },
  TZS: { name: 'Tanzanian Shilling', symbol: 'TZS' },
  UAH: { name: 'Ukrainian Hryvnia', symbol: '₴' },
  UGX: { name: 'Ugandan Shilling', symbol: 'UGX' },
  USD: { name: 'US Dollar', symbol: '$' },
  UYU: { name: 'Uruguayan Peso', symbol: 'UYU' },
  UZS: { name: 'Uzbekistani Som', symbol: 'UZS' },
  VES: { name: 'Venezuelan Bolívar', symbol: 'VES' },
  VND: { name: 'Vietnamese Dong', symbol: '₫' },
  VUV: { name: 'Vanuatu Vatu', symbol: 'VUV' },
  WST: { name: 'Samoan Tala', symbol: 'WST' },
  XAF: { name: 'Central African CFA Franc', symbol: 'XAF' },
  XCD: { name: 'East Caribbean Dollar', symbol: 'XCD' },
  XOF: { name: 'West African CFA Franc', symbol: 'XOF' },
  XPF: { name: 'CFP Franc', symbol: 'XPF' },
  YER: { name: 'Yemeni Rial', symbol: 'YER' },
  ZAR: { name: 'South African Rand', symbol: 'R' },
  ZMW: { name: 'Zambian Kwacha', symbol: 'ZMW' },
  ZWL: { name: 'Zimbabwean Dollar', symbol: 'ZWL' },
}

export const WORLD_CURRENCIES: Currency[] = Object.entries(RAW)
  .map(([code, { name, symbol }]) => ({
    id: code,
    code,
    name,
    sign: signFrom(code, symbol),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

export function getAllCurrencies(): Currency[] {
  return WORLD_CURRENCIES.map(c => new Currency(c.id, c.name, c.sign));
}

export function getCurrencyById(id: string): Currency | undefined {
  const upper = id.toUpperCase();
  return WORLD_CURRENCIES.find((c) => c.id === upper);
}
